import { useState, useCallback, useRef } from "react";

import { createFilePreviewUrl } from "@/utils/fileUtils";
import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { siteConfig } from "@/config/site";
import { logger } from "@/telemetry";

/**
 * Result of file fetch operation
 */
export interface FileFetchResult {
  /** ArrayBuffer containing file data (for documents requiring client-side rendering) */
  arrayBuffer: ArrayBuffer | null;
  /** Blob URL for preview (for standard file preview) */
  blobUrl: string | null;
  /** Detected/inferred MIME type */
  mimeType: string | null;
}

/**
 * Custom hook for fetching files with authentication
 * Centralises the logic for:
 * - Getting temporary download URLs
 * - Fetching with authentication
 * - Converting to ArrayBuffer or Blob URL
 * - Managing loading/error states
 * - Abort controller handling
 *
 * @example
 * ```tsx
 * const { fetchFile, data, loading, error, abort } = useAuthenticatedFileFetcher();
 *
 * // Fetch as ArrayBuffer (for Office docs, PDFs)
 * await fetchFile(fileId, fileName, { asArrayBuffer: true });
 *
 * // Fetch as Blob URL (for images, standard preview)
 * await fetchFile(fileId, fileName, { asArrayBuffer: false });
 * ```
 */
export function useAuthenticatedFileFetcher() {
  const { platformClient, authenticatedFetch } = useWebuildAPIClient();
  const [data, setData] = useState<FileFetchResult>({
    arrayBuffer: null,
    blobUrl: null,
    mimeType: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  /**
   * Abort the current fetch operation
   */
  const abort = useCallback(() => {
    if (abortControllerRef.current) {
      try {
        abortControllerRef.current.abort();
      } catch {
        // Ignore abort errors
      }
      abortControllerRef.current = null;
    }
  }, []);

  /**
   * Fetch a file with authentication
   *
   * @param fileId - ID of the file to fetch
   * @param fileName - Original filename for MIME type inference
   * @param options - Fetch options
   * @returns Promise that resolves when fetch completes
   */
  const fetchFile = useCallback(
    async (
      fileId: string,
      fileName: string,
      options: {
        /** If true, converts to ArrayBuffer. If false, returns Blob URL */
        asArrayBuffer?: boolean;
        /** Optional abort signal (if not provided, uses internal controller) */
        signal?: globalThis.AbortSignal;
      } = {},
    ): Promise<void> => {
      const { asArrayBuffer = false, signal } = options;

      try {
        // Abort previous request if using internal controller
        if (!signal) {
          abort();
        }

        // Create new abort controller if not provided
        const controller = signal ? undefined : new AbortController();

        if (controller) {
          abortControllerRef.current = controller;
        }

        // Set loading state
        setLoading(true);
        setError(null);
        setData({ arrayBuffer: null, blobUrl: null, mimeType: null });

        // Validate dependencies
        if (!platformClient) {
          throw new Error("Platform client not available");
        }

        if (!authenticatedFetch) {
          throw new Error("Authentication not available");
        }

        // Get temporary download URL
        const temp = await platformClient.files.createTemporaryDownloadUrl({
          fileId,
        });

        if (!temp?.temporary_url) {
          throw new Error("No temporary URL returned");
        }

        // Get base URL
        const baseUrl = (platformClient as any)?._baseURL ?? siteConfig().platformAPIURL ?? "";

        // Use centralised utility to fetch file
        const result = await createFilePreviewUrl(
          temp.temporary_url,
          baseUrl,
          authenticatedFetch,
          fileName,
          signal || controller?.signal,
        );

        if (!result.success) {
          throw new Error(result.error || "Failed to fetch file");
        }

        // Convert to ArrayBuffer if requested
        if (asArrayBuffer) {
          const response = await fetch(result.blobUrl!);
          const arrayBuffer = await response.arrayBuffer();

          setData({
            arrayBuffer,
            blobUrl: null,
            mimeType: result.mimeType || null,
          });

          // Clean up blob URL after conversion
          URL.revokeObjectURL(result.blobUrl!);
        } else {
          // Return blob URL for standard preview
          setData({
            arrayBuffer: null,
            blobUrl: result.blobUrl || null,
            mimeType: result.mimeType || null,
          });
        }

        setLoading(false);
      } catch (e: any) {
        // Handle abort gracefully
        if (e?.name === "AbortError") {
          setLoading(false);

          return;
        }

        logger.error("File fetch failed", e);
        setError(e.message || "Failed to fetch file");
        setLoading(false);
      }
    },
    [platformClient, authenticatedFetch, abort],
  );

  /**
   * Clean up blob URL to prevent memory leaks
   * Call this when component unmounts or when blob URL is no longer needed
   */
  const cleanup = useCallback(() => {
    if (data.blobUrl) {
      try {
        URL.revokeObjectURL(data.blobUrl);
      } catch {
        // Ignore revoke errors
      }
    }
    setData({ arrayBuffer: null, blobUrl: null, mimeType: null });
  }, [data.blobUrl]);

  return {
    /** Fetch a file with authentication */
    fetchFile,
    /** Fetched file data */
    data,
    /** Loading state */
    loading,
    /** Error message if fetch failed */
    error,
    /** Abort the current fetch operation */
    abort,
    /** Clean up blob URL to prevent memory leaks */
    cleanup,
  };
}
